import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Card,
  Form,
  Input,
  DatePicker,
  Button,
  Space,
  List,
  Alert,
  message,
  Skeleton,
  Tag,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faLocationDot,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import dayjs, { Dayjs } from "dayjs";
import {
  addCalendarEvent,
  getCalendarEventsList,
} from "../repository/data.repository";
import { CalendarEventListItem } from "../repository/data.default";
import { useTokenInfo } from "../hooks/useTokenInfo";
import { getProblemMessage } from "../utils/http";

// PD-02 §6.16.3 — Create Company Event. Posts a calendar event for the
// company so it shows up on the employee calendar. Audience is implicit via
// companyUid from the signed-in user's JWT.
function CreateEventPage() {
  const { claims } = useTokenInfo();
  const companyUid = claims?.usrCmpUid;
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [events, setEvents] = useState<CalendarEventListItem[]>([]);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    getCalendarEventsList({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 10,
      sortBy: "startDateTime",
      sortDescending: true,
    })
      .then((data) => setEvents(data.data ?? []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function onSubmit(values: {
    title: string;
    description?: string;
    range: [Dayjs, Dayjs];
    location?: string;
    onlineLink?: string;
  }) {
    setSubmitting(true);
    try {
      await addCalendarEvent({
        title: values.title,
        description: values.description,
        startDateTime: values.range[0].toISOString(),
        endDateTime: values.range[1].toISOString(),
        location: values.location,
        onlineLink: values.onlineLink,
        companyUid,
      });
      message.success("Event created.");
      form.resetFields();
      load();
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to create event.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Col className="animate__animated animate__fadeIn flex flex-auto flex-col gap-4 overflow-y-auto p-6">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faCalendarPlus} className="mr-2" />
          Create Company Event
        </Typography.Title>
      </Row>

      <Alert
        type="info"
        showIcon
        message="This event will land on the calendar of employees in your company."
      />

      <Card className="emp-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            range: [dayjs().add(1, "day").hour(10).minute(0), dayjs().add(1, "day").hour(11).minute(0)],
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="e.g. Mindfulness Workshop" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea
              rows={3}
              placeholder="What's the event about? (optional)"
            />
          </Form.Item>
          <Form.Item
            name="range"
            label="Date & time"
            rules={[{ required: true, message: "Date range is required" }]}
          >
            <DatePicker.RangePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              className="w-full"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="location" label="Location (optional)">
                <Input placeholder="e.g. Main meeting room" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="onlineLink" label="Online link (optional)">
                <Input placeholder="https://zoom.us/..." />
              </Form.Item>
            </Col>
          </Row>
          <Row className="justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create event
            </Button>
          </Row>
        </Form>
      </Card>

      <Card
        className="emp-card"
        title="Recent events"
        bodyStyle={{ padding: 0 }}
      >
        {loading ? (
          <div className="p-4">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : events.length === 0 ? (
          <div className="p-4">
            <Typography.Text type="secondary">
              No events yet. Create one above.
            </Typography.Text>
          </div>
        ) : (
          <List
            dataSource={events}
            renderItem={(e) => (
              <List.Item style={{ paddingLeft: 16, paddingRight: 16 }}>
                <List.Item.Meta
                  title={e.title}
                  description={
                    <Space direction="vertical" size={2} className="w-full">
                      <Typography.Text type="secondary" className="text-xs">
                        {dayjs(e.startDateTime).format("DD/MM/YYYY HH:mm")}
                        {e.endDateTime &&
                          ` – ${dayjs(e.endDateTime).format("HH:mm")}`}
                      </Typography.Text>
                      {e.description && (
                        <Typography.Text className="text-xs">
                          {e.description}
                        </Typography.Text>
                      )}
                      <Space wrap size={8}>
                        {e.location && (
                          <Tag
                            icon={<FontAwesomeIcon icon={faLocationDot} />}
                          >
                            {e.location}
                          </Tag>
                        )}
                        {e.onlineLink && (
                          <Tag
                            color="blue"
                            icon={<FontAwesomeIcon icon={faLink} />}
                            onClick={() =>
                              window.open(
                                e.onlineLink!,
                                "_blank",
                                "noopener",
                              )
                            }
                            style={{ cursor: "pointer" }}
                          >
                            Join online
                          </Tag>
                        )}
                      </Space>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </Col>
  );
}

export default CreateEventPage;
